/**
 * Map colours and the names, icons and colours of plan things, shared by the renderer, the legend and the panels.
 * Ground, plot, water, road and zone colours follow the shell's light/dark theme (CSS variables --map-*);
 * pictures and thumbnails always use the light colours so they look the same everywhere.
 */
import type { AnimalAreaKind, Biome, IconType, OtherAreaKind } from '../../shared/types';

/** Colours the renderer needs. */
export interface Palette {
  ground: string;
  plot: string;
  water: string;
  waterEdge: string;
  road: string;
  roadEdge: string;
  roadLoose: string;
  timber: string;
  timberEdge: string;
  plaza: string;
  boh: string;
  edge: string;
  aviary: string;
  aquarium: string;
  dark: boolean;
}

/** The light map colours (also used for pictures and thumbnails). */
export const LIGHT: Palette = {
  ground: '#e9e4d4',
  plot: '#c9dcae',
  water: '#6aaed6',
  waterEdge: '#2f6f99',
  road: '#efe6cc',
  roadEdge: '#6f6450',
  roadLoose: '#eab3a6',
  timber: '#a8794a',
  timberEdge: '#4a3420',
  plaza: '#e8d7b5',
  boh: '#b9b2a3',
  edge: '#5d6b4f',
  aviary: '#8fb3dd',
  aquarium: '#5fb9c2',
  dark: false,
};

/** Reads the map colours of the current theme. */
export function themePalette(): Palette {
  if (typeof document === 'undefined') return LIGHT;
  const cs = getComputedStyle(document.documentElement);
  const v = (name: string, fb: string) => cs.getPropertyValue(name).trim() || fb;
  const ground = v('--map-ground', LIGHT.ground);
  const dark = luminance(ground) < 0.35;
  return {
    ...LIGHT,
    ground,
    plot: dark ? '#3f5233' : LIGHT.plot,
    water: v('--map-water', LIGHT.water),
    plaza: v('--map-plaza', LIGHT.plaza),
    boh: v('--map-boh', LIGHT.boh),
    edge: v('--map-edge', LIGHT.edge),
    road: dark ? '#cdbf9c' : LIGHT.road,
    roadEdge: dark ? '#1f1c16' : LIGHT.roadEdge,
    aviary: v('--map-aviary', LIGHT.aviary),
    aquarium: v('--map-aquarium', LIGHT.aquarium),
    dark,
  };
}

function luminance(c: string): number {
  const m = /^#([0-9a-f]{6})$/i.exec(c.trim());
  if (!m) return 1;
  const n = parseInt(m[1]!, 16);
  return (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
}

/** Habitat colour per biome (the prototype's colours) and the legend name. */
export const BIOMES: Record<Biome, { label: string; color: string }> = {
  tropical: { label: 'Tropical', color: '#2f8f5b' },
  temperate: { label: 'Temperate', color: '#7d9b3c' },
  grassland: { label: 'Grassland', color: '#c9a43c' },
  desert: { label: 'Desert', color: '#d49a6a' },
  taiga: { label: 'Taiga', color: '#4f806c' },
  tundra: { label: 'Tundra', color: '#8fb4c9' },
  aquatic: { label: 'Water-loving', color: '#3f8fb0' },
};

/** Shop and service icons (the prototype's set). */
export const ICONS: Record<IconType, { label: string; icon: string; color: string }> = {
  entrance: { label: 'Entrance', icon: '🚪', color: '#34495e' },
  restaurant: { label: 'Restaurant', icon: '🍴', color: '#d35400' },
  gift: { label: 'Gift shop', icon: '🎁', color: '#8e44ad' },
  toilets: { label: 'Toilets', icon: '🚻', color: '#2980b9' },
  kiosk: { label: 'Snack kiosk', icon: '🥤', color: '#e67e22' },
  info: { label: 'Info point', icon: 'ℹ️', color: '#16a085' },
  staff: { label: 'Staff room', icon: '🔧', color: '#7f8c8d' },
  vet: { label: 'Vet', icon: '🩺', color: '#c0392b' },
};

/** Icons a person can place (the entrance comes with the plan). */
export const PLACEABLE_ICONS = (Object.keys(ICONS) as IconType[]).filter((t): t is Exclude<IconType, 'entrance'> => t !== 'entrance');

/** Plain names of the zone kinds. */
export const KIND_LABEL: Record<AnimalAreaKind | OtherAreaKind, string> = {
  habitat: 'Habitat',
  aviary: 'Aviary',
  aquarium: 'Aquarium',
  lake: 'Lake',
  pool: 'Pool',
  plaza: 'Entrance plaza',
  backOfHouse: 'Staff area',
};

/** Small picture shown with aviaries and aquariums. */
export const KIND_ICON: Partial<Record<AnimalAreaKind, string>> = { aviary: '🐦', aquarium: '🐠' };

/** `hex` lightened (f > 0) or darkened (f < 0). */
export function shade(hex: string, f: number): string {
  const n = parseInt(hex.slice(1), 16);
  const c = [n >> 16, (n >> 8) & 255, n & 255].map((v) => Math.round(f >= 0 ? v + (255 - v) * f : v * (1 + f)));
  return `rgb(${c.join(',')})`;
}

/** A small stable number from an id, for varying neighbouring habitat colours. */
export function idHash(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return Math.abs(h);
}
